/**
 * O BLOCO DE PESAGEM — a peça que cinco telas de manejo dividem.
 *
 * Conta, pesa, e repete. Aparece na biometria avulsa, na transferência (para
 * achar a média antes de mover), na despesca (a cada cestão) e na recepção
 * (nas amostras de alevino). Construída uma vez e herdada — a mesma regra do
 * módulo de cálculo: fórmula copiada em cinco arquivos diverge um dia.
 *
 * Aqui mora só a TELA. A aritmética mora em `dominio/pesagem.ts`, onde os
 * testes a alcançam sem precisar montar React.
 *
 * ── A TARA, E POR QUE ELA TEM TRÊS DEFESAS ────────────────────────────────
 *
 * O campo existe por causa de um acidente real: a balancinha da biometria
 * desliga a tela sozinha, alguém esquece de tarar, e o peso do cesto entra
 * somado ao do peixe. Nenhuma defesa sozinha resolve:
 *
 *   1. CAMPO PRÓPRIO, nunca embutido no peso. O que não tem campo não é
 *      lembrado.
 *   2. PRÉ-PREENCHIDA com a tara do bloco anterior, porque no campo o cesto é
 *      o mesmo a operação toda e redigitar é onde se erra.
 *   3. O PESO MÉDIO POR PEIXE, grande, enquanto se digita. Esta pega o erro
 *      contrário — o cesto que mudou e a tara que ficou. Quem está ali sabe de
 *      cabeça quanto pesa o peixe daquela represa, e uma média errada salta aos
 *      olhos antes de gravar.
 *
 * A terceira é a que vale mais, e é o mesmo mecanismo do total da quinzena na
 * ficha de papel: um número que a pessoa já conhece, conferindo os que ela
 * acabou de digitar.
 *
 * ── ONDE A TARA NÃO APARECE ───────────────────────────────────────────────
 *
 * Na despesca. A balança grande tem botão de tara e já entrega o líquido —
 * decisão do gestor em 20/09/2026. Campo que não serve rouba segundos de quem
 * está em pé no sol, e `comTara` o esconde.
 */

import { numero, numeroNaFaixa } from '../dominio/unidades'
import {
  apurar, apurarBloco, blocoVazio, pesoPorExtenso, porQueNaoApura, soNumero,
  type Apuracao, type Bloco,
} from '../dominio/pesagem'

// As telas de manejo importam bloco e conta deste mesmo lugar.
export { apurar, apurarBloco, blocoVazio, pesoPorExtenso }
export type { Apuracao, Bloco }

export function BlocosDePesagem({
  blocos, comTara = true, comContador = false, especies, rotulo = 'Pesagem', aoMudar,
}: {
  blocos: Bloco[]
  /** Falso na despesca: a balança grande tem botão de tara. */
  comTara?: boolean
  /**
   * Verdadeiro na despesca e na movimentação: ali a contagem vai de cinquenta
   * em cinquenta e termina num número menor que cinquenta. Os botões poupam a
   * digitação repetida; o campo continua aberto para a ponta final — pedido do
   * gestor em 20/09/2026, e ele está certo: contador sem campo digitável
   * obrigaria a apertar +1 quarenta e três vezes.
   */
  comContador?: boolean
  /** Presente só na despesca: a rede sai misturada e cada espécie tem preço. */
  especies?: readonly string[]
  /** «Pesagem» na biometria, «Cestão» na despesca. */
  rotulo?: string
  aoMudar: (blocos: Bloco[]) => void
}) {
  const total = apurar(blocos, comTara)

  function mudar(i: number, campo: keyof Omit<Bloco, 'chave'>, valor: string) {
    const copia = [...blocos]
    const atual = copia[i]
    if (!atual) return
    const limpo = campo === 'especie' ? valor : soNumero(valor)
    copia[i] = { ...atual, [campo]: limpo }
    aoMudar(copia)
  }

  /** O +50 e o +1. Soma ao que já está lá, e trata campo vazio como zero. */
  function somar(i: number, quanto: number) {
    const atual = blocos[i]
    if (!atual) return
    const agora = numeroNaFaixa(atual.quantidade, 0, 100_000) ?? 0
    mudar(i, 'quantidade', String(Math.round(agora) + quanto))
  }

  function acrescentar() {
    // A tara do último bloco vale para o próximo: é o mesmo cesto. A espécie
    // também: 99% é tambaqui, e trocar é a exceção.
    const ultimo = blocos[blocos.length - 1]
    aoMudar([
      ...blocos,
      blocoVazio(ultimo?.tara ?? '', especies ? (ultimo?.especie ?? especies[0]) : undefined),
    ])
  }

  return (
    <div className="pesagens">
      {blocos.map((b, i) => {
        const a = apurarBloco(b, comTara)
        const porque = porQueNaoApura(b, comTara)
        return (
          <div key={b.chave} className="pesagem">
            <div className="pesagem-topo">
              <span className="campo-rotulo">{rotulo} {i + 1}</span>
              {blocos.length > 1 && (
                <button
                  type="button"
                  className="discreto"
                  onClick={() => aoMudar(blocos.filter((_, j) => j !== i))}
                >
                  remover
                </button>
              )}
            </div>

            {especies && (
              <label className="campo">
                <span className="campo-rotulo">Espécie</span>
                <select
                  value={b.especie ?? especies[0] ?? ''}
                  onChange={(e) => mudar(i, 'especie', e.target.value)}
                >
                  {especies.map((x) => (
                    <option key={x} value={x}>{x}</option>
                  ))}
                </select>
              </label>
            )}

            <div className="pesagem-campos">
              <label className="campo">
                <span className="campo-rotulo">Peixes</span>
                <input
                  value={b.quantidade}
                  onChange={(e) => mudar(i, 'quantidade', e.target.value)}
                  inputMode="numeric"
                  placeholder="30"
                />
              </label>
              <label className="campo">
                <span className="campo-rotulo">{comTara ? 'Peso bruto (kg)' : 'Peso (kg)'}</span>
                <input
                  value={b.bruto}
                  onChange={(e) => mudar(i, 'bruto', e.target.value)}
                  inputMode="decimal"
                  placeholder="19,2"
                />
              </label>
              {comTara && (
                <label className="campo">
                  <span className="campo-rotulo">Tara (kg)</span>
                  <input
                    value={b.tara}
                    onChange={(e) => mudar(i, 'tara', e.target.value)}
                    inputMode="decimal"
                    placeholder="2,0"
                  />
                </label>
              )}
            </div>

            {comContador && (
              <div className="pesagem-contador">
                <button type="button" className="discreto" onClick={() => somar(i, 50)}>
                  +50
                </button>
                <button type="button" className="discreto" onClick={() => somar(i, 1)}>
                  +1
                </button>
                <button
                  type="button"
                  className="discreto"
                  onClick={() => mudar(i, 'quantidade', '')}
                >
                  zerar
                </button>
              </div>
            )}

            {/* O CONFERIDOR. Grande, e antes de gravar. */}
            <div className={a ? 'pesagem-media' : 'pesagem-media vazia'}>
              {a ? (
                <>
                  <strong>{pesoPorExtenso(a.mediaG)}</strong> por peixe
                  <span className="secundario">
                    {' · '}
                    {numero(a.liquidoKg, 2)} kg líquidos
                  </span>
                </>
              ) : (
                <span className="secundario">{porque ?? 'preencha peixes e peso'}</span>
              )}
            </div>
          </div>
        )
      })}

      <button type="button" className="discreto" onClick={acrescentar}>
        + {rotulo.toLowerCase()}
      </button>

      {/* O padrão, dito uma vez e no lugar onde se digita. */}
      <p className="pesagem-padrao secundario">
        Vírgula separa o decimal (19,2) · ponto separa o milhar (1.200)
      </p>

      {blocos.length > 1 && total && (
        <div className="pesagem-total">
          <div>
            <strong>{pesoPorExtenso(total.mediaG)}</strong> por peixe
          </div>
          <div className="secundario">
            {numero(total.peixes, 0)} peixes · {numero(total.liquidoKg, 2)} kg — é esta
            média que vai para o banco
          </div>
        </div>
      )}
    </div>
  )
}
